import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import SocialPostsService from "@app/services/http/social-posts.service";
import {
  SocialPostDetail,
  SocialPostComment,
} from "@app/types/social-post.type";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

import { addToast } from "@app/components/toast/toast.service";
import { BUCKET_URL_KEY, SystemMessage, REGEX_URL } from "@app/constants";
import StorageService from "@core/services/storage";
import TickCircleIcon from "@app/icons/tick-circle";
import AddIcon from "@app/icons/add";
import Heart2Icon from "@app/icons/heart_2";
import Message2Icon from "@app/icons/message_2";
import RepeatIcon from "@app/icons/repeat";
import ShareIcon from "@app/icons/share";
import EditIcon from "@app/icons/edit";
import DeleteIcon from "@app/icons/delete";
import { DeviceFrameset } from "react-device-frameset";

import "react-device-frameset/styles/marvel-devices.min.css";

function CommentFeedManagementPage() {
  const { feedId } = useParams();
  const [socialPost, setSocialPost] = useState<SocialPostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const s3Url = useMemo(() => StorageService.getObject(BUCKET_URL_KEY), []);

  useEffect(() => {
    if (feedId) {
      loadSocialPost();
    }
  }, [feedId]);

  const loadSocialPost = async () => {
    try {
      setLoading(true);
      const response = await SocialPostsService.getDetail(Number(feedId), {
        isGetComments: true,
      });

      setSocialPost(response.data);
    } catch (error) {
      // Handle error silently or show toast
      addToast({ text: "Có lỗi xảy ra khi tải dữ liệu" });
    } finally {
      setLoading(false);
    }
  };

  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");

  const buildSrc = (url?: string) => {
    if (!url) return "";

    return url.match(REGEX_URL) ? url : s3Url + url;
  };

  const handleEditComment = (comment: SocialPostComment) => {
    setEditingComment(comment.id);
    setEditContent(comment.content || "");
  };

  const handleCancelEditComment = () => {
    setEditingComment(null);
    setEditContent("");
  };

  const handleSaveEditComment = async (comment: SocialPostComment) => {
    if (!socialPost || !editContent.trim()) return;

    try {
      await SocialPostsService.updateComment(socialPost.id, comment.id, {
        content: editContent,
      });
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      setEditingComment(null);
      setEditContent("");
      loadSocialPost();
    } catch (error) {
      addToast({ text: "Có lỗi xảy ra khi cập nhật comment" });
    }
  };

  const handleDeleteComment = (comment: SocialPostComment) => {
    if (!socialPost) return;

    const confirmModalObs = openPortalDialog(ConfirmModal, {
      message: "Bạn có chắc chắn muốn xóa comment này không?",
    });

    confirmModalObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        SocialPostsService.deleteComment(socialPost.id, comment.id).then(() => {
          addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
          loadSocialPost();
        });
      }
    });
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Vừa xong";

    if (hours < 24) return `${hours} giờ`;

    if (days < 30) return `${days} ngày`;

    // Tính tháng và năm chính xác hơn
    const date = new Date(timestamp);
    const currentDate = new Date();

    const yearDiff = currentDate.getFullYear() - date.getFullYear();
    const monthDiff = currentDate.getMonth() - date.getMonth();

    let totalMonths = yearDiff * 12 + monthDiff;

    // Điều chỉnh nếu ngày trong tháng chưa đến
    if (currentDate.getDate() < date.getDate()) {
      totalMonths--;
    }

    if (totalMonths < 12) {
      return `${totalMonths} tháng`;
    }

    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;

    if (remainingMonths === 0) {
      return `${years} năm`;
    }

    return `${years} năm ${remainingMonths} tháng`;
  };

  if (loading) {
    return (
      <div className="bg-[#f4f5fa] w-full h-full min-h-[calc(100vh-100px)] p-6 flex items-center justify-center">
        <div>Đang tải...</div>
      </div>
    );
  }

  if (!socialPost) {
    return (
      <div className="bg-[#f4f5fa] w-full h-full min-h-[calc(100vh-100px)] p-6 flex items-center justify-center">
        <div>Không tìm thấy bài viết</div>
      </div>
    );
  }

  const renderComment = (comment: SocialPostComment, level: number = 0) => {
    const paddingClass = level === 0 ? "" : level === 1 ? "pl-10" : "pl-20";
    const connectorPosition =
      level === 1 ? "left-5 -top-14" : "left-[59px] -top-[55px]";

    return (
      <div key={comment.id}>
        <div className={`relative flex gap-4 ${paddingClass}`}>
          {level > 0 && (
            <div
              className={`pointer-events-none absolute ${connectorPosition} h-[68px] w-6`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="69"
                viewBox="0 0 25 69"
                fill="none"
              >
                <path
                  d="M1 0V44C1 57.2548 11.7452 68 25 68V68"
                  stroke="#D5DBE2"
                  strokeWidth="2"
                />
              </svg>
            </div>
          )}
          <img
            className="size-14 rounded-full"
            alt="avatar"
            src={buildSrc(comment.authorThumbnail)}
          />
          <div className="flex grow gap-3">
            <div className="flex grow flex-col gap-1 justify-center">
              <div className="flex items-center gap-2">
                <p
                  className="text-[#171a20] text-[18px] leading-[26px] tracking-[0.027px]"
                  style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                >
                  {comment.authorName}
                </p>
                <p
                  className="text-[#8495ac] text-[16px] leading-[24px] tracking-[0.024px]"
                  style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                >
                  {formatTimeAgo(comment.createdDate)}
                </p>
              </div>
              {editingComment === comment.id ? (
                <div className="mt-2">
                  <div className="bg-white border border-[#d5dbe2] rounded-2xl p-4">
                    <textarea
                      className="w-full h-[86px] border-none outline-none resize-none rounded-2xl text-[#171a20] text-[16px] leading-[24px]"
                      style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      placeholder="Nhập nội dung comment..."
                    />
                  </div>
                  <div className="flex gap-4 justify-end mt-4">
                    <button
                      className="border border-[#5a82d7] rounded-[20px] px-4 py-2 w-[136px] text-transparent bg-clip-text bg-gradient-to-b from-[#5a82d7] to-[#4667ca]"
                      style={{
                        fontFamily: "'SVN-Gilroy', sans-serif",
                        fontWeight: 700,
                      }}
                      onClick={handleCancelEditComment}
                    >
                      Huỷ
                    </button>
                    <button
                      className="bg-gradient-to-b from-[#5a82d7] to-[#4667ca] text-white rounded-[20px] px-4 py-2 w-[136px]"
                      style={{
                        fontFamily: "'SVN-Gilroy', sans-serif",
                        fontWeight: 700,
                      }}
                      onClick={() => handleSaveEditComment(comment)}
                    >
                      Cập nhật
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className="text-[#171a20] text-[16px] leading-[24px]"
                  style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                >
                  {comment.content}
                </p>
              )}
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-1">
                  <Message2Icon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {comment.commentCount}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart2Icon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {comment.likeCount}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div
                className="cursor-pointer"
                onClick={() => handleEditComment(comment)}
              >
                <EditIcon />
              </div>
              <div
                className="cursor-pointer"
                onClick={() => handleDeleteComment(comment)}
              >
                <DeleteIcon />
              </div>
            </div>
          </div>
        </div>
        {comment.children?.map((child) => renderComment(child, level + 1))}
      </div>
    );
  };

  const renderCommentPreview = (
    comment: SocialPostComment,
    level: number = 0
  ) => {
    const paddingClass = level === 0 ? "" : level === 1 ? "pl-10" : "pl-20";
    const connectorPosition =
      level === 1 ? "left-5 -top-14" : "left-[59px] -top-[55px]";

    const previewContent =
      editingComment === comment.id ? editContent : comment.content;

    return (
      <div key={`preview-${comment.id}`}>
        <div className={`relative flex gap-4 ${paddingClass}`}>
          {level > 0 && (
            <div
              className={`pointer-events-none absolute ${connectorPosition} h-[68px] w-6`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="69"
                viewBox="0 0 25 69"
                fill="none"
              >
                <path
                  d="M1 0V44C1 57.2548 11.7452 68 25 68V68"
                  stroke="#D5DBE2"
                  strokeWidth="2"
                />
              </svg>
            </div>
          )}
          <img
            className="size-10 rounded-full"
            alt="avatar"
            src={buildSrc(comment.authorThumbnail)}
          />
          <div className="flex grow gap-3">
            <div className="flex grow flex-col gap-1 justify-center">
              <div className="flex items-center gap-2">
                <p
                  className="text-[#171a20] text-[14px] leading-[22px] tracking-[0.02px]"
                  style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                >
                  {comment.authorName}
                </p>
                <p
                  className="text-[#8495ac] text-[12px] leading-[18px] tracking-[0.02px]"
                  style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                >
                  {formatTimeAgo(comment.createdDate)}
                </p>
              </div>
              <p
                className="text-[#171a20] text-[14px] leading-[22px]"
                style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
              >
                {previewContent}
              </p>
              <div className="flex items-center gap-5 mt-1">
                <div className="flex items-center gap-1">
                  <Message2Icon />
                  <span
                    className="text-[#657892] text-[12px] leading-[18px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {comment.commentCount}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart2Icon />
                  <span
                    className="text-[#657892] text-[12px] leading-[18px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {comment.likeCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {comment.children?.map((child) =>
          renderCommentPreview(child, level + 1)
        )}
      </div>
    );
  };

  return (
    <div className="bg-[#f4f5fa] w-full h-full min-h-[calc(100vh-100px)] py-6 px-12">
      <div className="flex gap-6 items-start">
        {/* Left column */}
        <div className="bg-white flex-1 min-w-0 rounded-md">
          {/* Post block */}
          <div className="px-4 pt-4 pb-3">
            <div className="border-b border-[#d5dbe2] pb-4">
              {/* Post header */}
              <div className="flex items-start gap-2">
                <div className="flex grow items-center gap-5">
                  <img
                    alt="avatar"
                    className="size-[72px] rounded-full"
                    src={buildSrc(socialPost.authorThumbnail)}
                  />
                  <div className="flex items-center gap-2">
                    <p
                      className="font-[600] text-[24px] leading-[26px] tracking-[0.036px] text-[#171a20]"
                      style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                    >
                      {socialPost.authorName}
                    </p>
                    {socialPost.isRoleAuthor && <TickCircleIcon />}
                    <p
                      className="text-[#8495ac] text-[16px] leading-[24px] tracking-[0.024px]"
                      style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                    >
                      {formatTimeAgo(socialPost.createdDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="rounded-[20px] bg-gradient-to-b from-[#5a82d7] to-[#4667ca] text-white flex items-center gap-1 pl-2 pr-4 py-2">
                    <AddIcon />
                    <span
                      className="text-[14px] font-bold tracking-[0.028px]"
                      style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                    >
                      Theo dõi
                    </span>
                  </button>
                </div>
              </div>
              {/* Post content */}
              <div
                className="mt-6 text-[#171a20] text-[20px] leading-[24px]"
                style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
              >
                <p>{socialPost.content}</p>
              </div>
              {/* Post actions */}
              <div className="mt-4 flex items-end gap-6">
                <div className="flex items-center gap-1">
                  <Heart2Icon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {socialPost.likeCount}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Message2Icon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {socialPost.commentCount}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <RepeatIcon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {socialPost.repostCount}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <ShareIcon />
                  <span
                    className="text-[#657892] text-[16px] leading-[24px]"
                    style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                  >
                    {socialPost.shareCount}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Comments list */}
          <div className="px-4 pb-6 flex flex-col gap-6">
            {socialPost.postComment?.items?.map((comment) =>
              renderComment(comment)
            )}
          </div>
        </div>

        {/* Right column - Mobile live preview using react-device-frameset */}
        <div className="w-[28vw] max-w-[400px] min-w-[320px]">
          <div className="fixed right-12 top-[100px] bottom-6 w-[28vw] max-w-[400px] min-w-[320px]">
            <DeviceFrameset
              device="iPhone X"
              color="black"
              landscape={false}
              zoom={0.8}
            >
              <div className="h-full overflow-y-auto bg-white pt-6">
                <div className="p-4 border-b border-[#eef0f6]">
                  <div className="flex items-start gap-3">
                    <img
                      alt="avatar"
                      className="size-10 rounded-full"
                      src={buildSrc(socialPost.authorThumbnail)}
                    />
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <p
                          className="font-[600] text-[14px] leading-[20px] tracking-[0.02px] text-[#171a20]"
                          style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                        >
                          {socialPost.authorName}
                        </p>
                        {socialPost.isRoleAuthor && <TickCircleIcon />}
                        <p
                          className="text-[#8495ac] text-[12px] leading-[18px] tracking-[0.02px]"
                          style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                        >
                          {formatTimeAgo(socialPost.createdDate)}
                        </p>
                      </div>
                      <div
                        className="text-[#171a20] text-[14px] leading-[20px]"
                        style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      >
                        <p>
                          {editingComment === -1
                            ? editContent
                            : socialPost.content}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Heart2Icon />
                      <span
                        className="text-[#657892] text-[12px] leading-[18px]"
                        style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      >
                        {socialPost.likeCount}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Message2Icon />
                      <span
                        className="text-[#657892] text-[12px] leading-[18px]"
                        style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      >
                        {socialPost.commentCount}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <RepeatIcon />
                      <span
                        className="text-[#657892] text-[12px] leading-[18px]"
                        style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      >
                        {socialPost.repostCount}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ShareIcon />
                      <span
                        className="text-[#657892] text-[12px] leading-[18px]"
                        style={{ fontFamily: "'SVN-Gilroy', sans-serif" }}
                      >
                        {socialPost.shareCount}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 flex flex-col gap-4">
                  {socialPost.postComment?.items?.map((comment) =>
                    renderCommentPreview(comment)
                  )}
                </div>
              </div>
            </DeviceFrameset>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommentFeedManagementPage;
